import type { SyntheticEvent } from 'react'

/** Assert that value is element of array  */
export function isElementOfArray<T>(
  arr: readonly T[],
  value: unknown,
): value is T {
  return arr.includes(value as T)
}
export function isString(value: unknown): value is string {
  return typeof value === 'string'
}
export function isNotEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}
export function isNumber(value: unknown): value is number {
  return typeof value === 'number' && !isNaN(value) && value !== Infinity
}
export function ensureValidNumber(value: unknown, fallback?: number): number {
  if (isNumber(value)) {
    return value
  }
  const num = parseInt(String(value), 10)
  if (isNumber(num)) {
    return num
  }

  if (fallback !== undefined) {
    return fallback
  }
  throw new Error('Not a number')
}
export function isPositiveNumber(value: unknown): value is number {
  return isNumber(value) && value > 0
}
export function maybeNumber(value: unknown): number | undefined {
  if (isNumber(value)) {
    return value
  }
  if (typeof value === 'string') {
    const num = parseInt(value, 10)
    return isNumber(num) ? num : undefined
  }
}

export function isHTMLOrSVGElement(value: unknown): value is HTMLOrSVGElement {
  return isHTMLElement(value) || isSVGElement(value)
}
export function isHTMLElement(value: unknown): value is HTMLElement {
  if (value instanceof HTMLElement) {
    return true
  }
  return false
}
export function isSVGElement(value: unknown): value is SVGElement {
  if (value instanceof SVGElement) {
    return true
  }
  return false
}
export function isHTMLInputElement(
  el?: Element | EventTarget,
): el is HTMLInputElement {
  if (el && (el as any).tagName === 'INPUT') {
    return true
  }
  return false
}

export type satisfy<base, t extends base> = t

/**
 * Asserts that value is a React event
 */
export function isReactEvent(value: unknown): value is SyntheticEvent {
  return (
    value instanceof Object &&
    '_reactName' in value &&
    Object.getPrototypeOf(value).constructor.name === 'SyntheticBaseEvent'
  )
}
